import { useCallback, useEffect, useState } from 'react';
import { fetchShops, searchBookings } from '../utils/api';
import type { BookingSearchResult } from '../models/repair-bookings';

export type BookingSearchFormState = {
    firstNamePronunciation: string;
    lastNamePronunciation: string;
    phoneNumber: string;
    confirmationId: string;
    shopName: string;
    fromDate: string;
    toDate: string;
};

type BookingSearchTextField = Exclude<keyof BookingSearchFormState, 'fromDate' | 'toDate'>;

const createInitialState = (): BookingSearchFormState => {
    return {
        firstNamePronunciation: '',
        lastNamePronunciation: '',
        phoneNumber: '',
        confirmationId: '',
        shopName: '',
        fromDate: '',
        toDate: '',
    };
};

const useBookingSearch = (csrfToken: string) => {
    const [form, setForm] = useState<BookingSearchFormState>(createInitialState);
    const [bookings, setBookings] = useState<BookingSearchResult[] | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [shopNames, setShopNames] = useState<string[]>([]);

    useEffect(() => {
        const loadShops = async () => {
            try {
                const response = await fetchShops(csrfToken);
                setShopNames(response.shops.map((s) => s.name));
            } catch {
                /* do nothing */
            }
        };
        void loadShops();
    }, [csrfToken]);

    const changeTextField = useCallback((field: BookingSearchTextField, value: string) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const onLastNamePronunciationChange = useCallback(
        (value: string) => {
            changeTextField('lastNamePronunciation', value);
        },
        [changeTextField]
    );

    const onFirstNamePronunciationChange = useCallback(
        (value: string) => {
            changeTextField('firstNamePronunciation', value);
        },
        [changeTextField]
    );

    const onPhoneNumberChange = useCallback(
        (value: string) => {
            changeTextField('phoneNumber', value);
        },
        [changeTextField]
    );

    const onConfirmationIdChange = useCallback(
        (value: string) => {
            changeTextField('confirmationId', value);
        },
        [changeTextField]
    );

    const onShopNameChange = useCallback(
        (value: string) => {
            changeTextField('shopName', value);
        },
        [changeTextField]
    );

    const onDateRangeChange = useCallback((fromDate: string, toDate: string) => {
        setForm((prev) => ({
            ...prev,
            fromDate,
            toDate,
        }));
    }, []);

    const search = useCallback(async () => {
        setIsSearching(true);
        try {
            const response = await searchBookings(csrfToken, {
                first_name_pronunciation: form.firstNamePronunciation.trim(),
                last_name_pronunciation: form.lastNamePronunciation.trim(),
                phone_number: form.phoneNumber.trim(),
                confirmation_id: form.confirmationId.trim(),
                shop_name: form.shopName.trim(),
                from_date: form.fromDate.trim(),
                to_date: form.toDate.trim(),
            });
            setBookings(response.bookings);
        } catch (err) {
            setError(err as Error);
        } finally {
            setIsSearching(false);
        }
    }, [csrfToken, form]);

    const clearSearch = useCallback(() => {
        setBookings(null);
    }, []);

    return {
        form,
        bookings,
        isSearching,
        error,
        shopNames,
        onLastNamePronunciationChange,
        onFirstNamePronunciationChange,
        onPhoneNumberChange,
        onConfirmationIdChange,
        onShopNameChange,
        onDateRangeChange,
        search,
        clearSearch,
    };
};

export default useBookingSearch;
